import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
	AppBar,
	Toolbar,
	IconButton,
	Avatar,
	Menu,
	MenuItem,
	Button,
	useMediaQuery,
	Box,
	Divider,
	Drawer,
	ListItemIcon,
	alpha,
	Typography,
	Tooltip,
	Dialog,
} from "@mui/material";
import {
	Menu as MenuIcon,
	AccountCircle as AccountCircleIcon,
	EmailOutlined as EmailOutlinedIcon,
	ArrowDropDown as ArrowDropDownIcon,
	Person as PersonIcon,
	Logout as LogoutIcon,
	Inventory as InventoryIcon,
	Settings,
	HelpOutline as HelpOutlineIcon,
	School as SchoolIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import SiteSelection from "../SiteSelection/SiteSelection";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import CartIcon from "../CartIcon/CartIcon";
import { useAuth } from "../../context/authContext";
import "./Header.css";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useTranslation } from "react-i18next";
import { LABELS, MENU_ITEMS, ROUTES, UI_TEXT } from "../../constants";
import { stringAvatar } from "../../utilities/globalServiceUtils";
import UserName from "../UserName/UserName";
import ContactCCRForm from "../ContactCCRForm/ContactCCRForm";
import NotificationBell from "../Notifications/NotificationBell";
import { toast } from "../../context/ToastContext";
import HelpDirectory from "../HelpDirectory/HelpDirectory";
import { useTour } from "../../context/TourContext";
import CompanyLogo from "../CompanyLogo/CompanyLogo";
import { DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from "../../config";
import { buildExternalLoginUrl } from "../../utilities/externalDocs";
import { useSite } from "../../context/siteContext";
import { useOptions } from "../../context/optionContext";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
import { canAccessOrderAnalytics, canAccessUserAnalytics } from "../../utilities/pageAccess";

const Header = ({ chatOpen, setChatOpen, showChatbot } = {}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [anchorEl, setAnchorEl] = useState(null);
	const [profileAnchorEl, setProfileAnchorEl] = useState(null);
	const [activeMobileSubmenu, setActiveMobileSubmenu] = useState(null);
	const [mobileOpen, setMobileOpen] = useState(false);
	const isMobile = useMediaQuery("(max-width:1200px)");
	const { user, hasPermission } = useAuth();
	const { t } = useTranslation();
	const { optionKeys } = useOptions();
	const enabledAdminModuleSet = React.useMemo(
		() => new Set(resolveEnabledAdminModules(optionKeys)),
		[optionKeys]
	);

	const [menuIndex, setMenuIndex] = React.useState(null);
	const [ccrOpen, setCcrOpen] = useState(false);
	const [helpOpen, setHelpOpen] = useState(false);

	const { startTour, hasTourSteps } = useTour();
	const { selectedSiteData } = useSite();

	const hideSearchAndSiteSelect =
		location.pathname.startsWith(ROUTES.PROFILE) || location.pathname.startsWith(ROUTES.SUPPORT);

	const handleMenuClick = (event, index, path) => {
		setAnchorEl(event.currentTarget);
		setMenuIndex(index);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuIndex(null);
	};

	const handleProfileClick = (event, path) => {
		setProfileAnchorEl(profileAnchorEl ? null : event.currentTarget);
	};

	const handleProfileClose = (path) => {
		setProfileAnchorEl(null);
		if (typeof path === "string") {
			navigate(path);
		}
	};

	const handleHelpOpen = () => {
		setHelpOpen(true);
	};

	const handleHelpClose = () => {
		setHelpOpen(false);
	};

	const handleStartTour = () => {
		if (hasTourSteps()) {
			startTour();
		}
	};

	const handleDocumentationNavigate = () => {
		if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
			navigate(ROUTES.DOCUMENT);
			return;
		}
		const username = selectedSiteData?.amf_method_username_ln;
		const password = selectedSiteData?.amf_method_password_ln;
		const loginUrl = buildExternalLoginUrl({
			templateUrl: DOCUMENT_EXTERNAL_URL,
			username,
			password,
		});
		if (!loginUrl) {
			navigate(ROUTES.DOCUMENT);
			return;
		}
		window.open(loginUrl, "_blank", "noopener,noreferrer");
	};

	const allNavlinks = [
		{ label: t('LABELS.HOME'), path: ROUTES.HOME_ALIAS },
		{ label: t('LABELS.MACHINESS'), path: ROUTES.MACHINES },
		{ label: t('LABELS.DOCUMENTATION'), path: ROUTES.DOCUMENT },
		{
			label: t('LABELS.MY_LISTS'),
			path: null,
			subMenu: [
				{ label: t('LABELS.RSPL'), path: ROUTES.RSPL },
				{ label: t('LABELS.RECOMMENDATION'), path: ROUTES.RECOMMENDATIONS },
				{ label: t('LABELS.REQUEST'), path: ROUTES.REQUESTS },
				{ label: t('LABELS.SAVED_FOR_LATERS'), path: ROUTES.SAVED_FOR_LATER },
			],
		},
		{
			label: t('LABELS.ANALYTIC'),
			path: null,
			subMenu: [
				{ label: t("ITEMS_TEXT.ORDER_ANALYTICS", { defaultValue: "Order Analytics" }), path: ROUTES.ANALYTICS },
				{ label: t("ITEMS_TEXT.USER_ANALYTICS", { defaultValue: "User Analytics" }), path: ROUTES.USER_ANALYTICS },
			],
		},
		{ label: t('LABELS.SERVICES'), path: ROUTES.SERVICES },
	];

	const isSuperAdmin = user?.user_id === 1;
	const isAdminRole = (user?.role?.role_name === 'Admin' || user?.role?.role_type === 'Admin') || user?.user_role === 'Admin';
	const canAccessAdminPage = isAdminRole || isSuperAdmin;
	const canSeeOrderAnalytics =
		canAccessOrderAnalytics(hasPermission, user) &&
		(isSuperAdmin || enabledAdminModuleSet.has("ITEMS_TEXT.ORDER_ANALYTICS"));
	const canSeeUserAnalytics =
		canAccessUserAnalytics(hasPermission, user) &&
		(isSuperAdmin || enabledAdminModuleSet.has("ITEMS_TEXT.USER_ANALYTICS"));
	const canSeeAnalytics = canSeeOrderAnalytics || canSeeUserAnalytics;
	const canSeeServices = !!hasPermission("service_management.view_cases") || !!hasPermission("service_management.view_workorders");
	const canSeeRequests = !!hasPermission("requests_management.allow_notify_customer_care");
	const canSeeSavedForLater = !!hasPermission("order_management.add_to_cart");
	const canSeeRecommendations = !!hasPermission("recommendations_management.add_parts")
		|| !!hasPermission("recommendations_management.create_list")
		|| !!hasPermission("recommendations_management.edit_list")
		|| !!hasPermission("recommendations_management.delete_list");

	const filterSubMenu = (subMenu = []) =>
		subMenu.filter((subLink) => {
			switch (subLink.path) {
				case ROUTES.REQUESTS:
					return canSeeRequests;
				case ROUTES.SAVED_FOR_LATER:
					return canSeeSavedForLater;
				case ROUTES.RSPL:
				case ROUTES.RECOMMENDATIONS:
					return canSeeRecommendations;
				case ROUTES.ANALYTICS:
					return canSeeOrderAnalytics;
				case ROUTES.USER_ANALYTICS:
					return canSeeUserAnalytics;	
				default:
					return true;
			}
		});

	const Navlinks = allNavlinks
		.map((link) => (link.subMenu ? { ...link, subMenu: filterSubMenu(link.subMenu) } : link))
		.filter((link) => {
			if (link.subMenu?.some((subLink) => subLink.path === ROUTES.ANALYTICS || subLink.path === ROUTES.USER_ANALYTICS)) return canSeeAnalytics;
			if (link.path === ROUTES.SERVICES) return canSeeServices;
			if (!link.path && link.subMenu) return link.subMenu.length > 0;
			return true;
		});

	const toggleMobileMenu = () => setMobileOpen(!mobileOpen);

	const handleMobileNavClick = (link, event) => {
		event.stopPropagation();

		if (link.subMenu) {
			setActiveMobileSubmenu(activeMobileSubmenu === link.label ? null : link.label);
		} else {
			if (link.path === ROUTES.DOCUMENT) {
				handleDocumentationNavigate();
			} else {
				navigate(link.path);
			}
			setActiveMobileSubmenu(null);
		}
	};
	const settingsPermissionKeys = [
		'app_settings.manage_settings',
		'app_settings.manage_order_settings',
		'app_settings.manage_inventory_settings',
		'app_settings.manage_site_settings',
		'app_settings.manage_reminder_settings',
		'app_settings.manage_cart_retention',
	];
	const canManageSettings = settingsPermissionKeys.some((perm) => hasPermission(perm));

	const canSeeAdmin = (() => {
		if (isSuperAdmin) return true;
		const checks = [
			["ITEMS_TEXT.USERS", () => hasPermission('user_management.create_users') || hasPermission('user_management.edit_users') || hasPermission('user_management.delete_users')],
			["ITEMS_TEXT.ROLES", () => hasPermission('role_management.create_roles') || hasPermission('role_management.edit_roles') || hasPermission('role_management.delete_roles')],
			["ITEMS_TEXT.SITES", () => hasPermission('site_management.edit_sites')],
			["ITEMS_TEXT.BANNERS", () => hasPermission('banner_management.view_banners') || hasPermission('banner_management.create_banners') || hasPermission('banner_management.edit_banners') || hasPermission('banner_management.delete_banners')],
			["ITEMS_TEXT.DOCUMENTS", () => hasPermission('document_management.create_documents') || hasPermission('document_management.edit_documents') || hasPermission('document_management.delete_documents')],
			["ITEMS_TEXT.MACHINE_PROVISION", () => hasPermission('machine_provision_management.provision_machines') || hasPermission('machine_provision_management.edit_provisions') || hasPermission('machine_provision_management.delete_provisions')],
			["ITEMS_TEXT.PRICE_MARKUPS", () => hasPermission('app_settings.manage_price_markups')],
			["ITEMS_TEXT.LINE_DISCOUNTS", () => hasPermission('line_discount_management.view_line_discounts') || hasPermission('line_discount_management.create_line_discounts') || hasPermission('line_discount_management.edit_line_discounts') || hasPermission('line_discount_management.delete_line_discounts')],
			["ITEMS_TEXT.PROMOTIONS", () => hasPermission('promotion_management.view_promotions') || hasPermission('promotion_management.create_promotions') || hasPermission('promotion_management.edit_promotions') || hasPermission('promotion_management.delete_promotions')],
			["ITEMS_TEXT.CHIP_REVIEW_APPROVAL", () => hasPermission('chip_governance.manage_chip_rules')],
			["ITEMS_TEXT.UPLOAD_MODELS", () => hasPermission('app_settings.allow_model_uploads')],
			["ITEMS_TEXT.SETTINGS", () => canManageSettings || hasPermission('app_settings.manage_default_values')],
			["ITEMS_TEXT.POLICY_CENTER", () => hasPermission('app_settings.manage_policy_center')],
		];
		return checks.some(([key, check]) => enabledAdminModuleSet.has(key) && check());
	})();

	let profileMenuItems = [];

	profileMenuItems.push(
		{ label: t("LABELS.PROFILE"), path: ROUTES.PROFILE, icon: <PersonIcon fontSize="small" />, divider: false },
		{ label: t("LABELS.SUPPORT"), path: ROUTES.SUPPORT, icon: <EmailOutlinedIcon fontSize="small" />, divider: false }
	);

	const canSeeOrderHistory = !!hasPermission('order_management.view_order_history');
	const orderSchedulesEnabled = optionKeys?.enableOrderSchedules !== "false";
	const canSeeOrderSchedules =
		orderSchedulesEnabled &&
		(!!hasPermission('order_management.create_order_schedules')
			|| !!hasPermission('order_management.edit_order_schedules')
			|| !!hasPermission('order_management.delete_order_schedules'));

	if (canSeeOrderHistory || canSeeOrderSchedules) {
		const menuItem = {
			label: canSeeOrderHistory
				? t("LABELS.ORDER_HISTORYS")
				: t("LABELS.ORDER_SCHEDULES"),
			path: ROUTES.ORDER_HISTORY,
			icon: <InventoryIcon fontSize="small" />,
			divider: true,
		};
		profileMenuItems.push(menuItem);
	}

	if (canSeeAdmin && canAccessAdminPage) {
		profileMenuItems.push({
			label: t("MENU_ITEMS.ADMIN"),
			path: ROUTES.ADMIN,
			icon: <AdminPanelSettingsIcon fontSize="small" />,
			divider: true,
		});
	}

	profileMenuItems.push({
		label: t("LABELS.LOGOUT"),
		path: ROUTES.LOGOUT,
		icon: <LogoutIcon fontSize="small" />,
		divider: true,
	});

	const labelToRouteMap = profileMenuItems.reduce((map, item) => {
		map[item.label] = item.path;
		return map;
	}, {});

	const handleProfileNavigate = (label) => {
		const route = labelToRouteMap[label];

		if (route === ROUTES.ADMIN && !canAccessAdminPage) {
			setProfileAnchorEl(null);
			return;
		}

		const isViewer = (user?.role?.role_name === 'Viewer' || user?.role?.role_type === 'Viewer') || user?.user_role === 'Viewer';
		if ((route === ROUTES.ANALYTICS || route === ROUTES.USER_ANALYTICS)&& isViewer) {
			setProfileAnchorEl(null);
			return;
		}

		handleProfileClose();

		if (route === ROUTES.LOGOUT) {
			toast.trigger({ message: t('UI_TEXT.LOGGING_OUT'), severity: "warning" });
			setTimeout(() => {
				navigate(route);
			}, 2000);
		} else if (route) {
			navigate(route);
		} else {
			const fallbackPath = `/${label.toLowerCase().replace(/\s+/g, "-")}`;
			navigate(fallbackPath);
		}
	}

	return (
		<AppBar position="sticky" color="default" className="header-main">
			<Toolbar className="header-toolbar" sx={{ px: '10px !important' }}>
				<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: 'auto', width: '150px' }}>
					<CompanyLogo alt="logo" onClick={() => navigate(ROUTES.HOME_ALIAS)} style={{ cursor: "pointer", maxWidth: "140px" }} skeletonWidth={140} skeletonHeight={40} />
				</Box>

				{!isMobile ? (
					<>
						<Box
							className="header-links"
							data-tour="main-page-navbar"
							component="nav"
						>
							{Navlinks.map((link, index) =>
								link.subMenu ? (
									<Box key={index} component="div">
										<Button
											color="inherit"
											sx={{ px: "8px" }}
											onClick={(event) => handleMenuClick(event, index)}
										>
											{link.label} <ArrowDropDownIcon />
										</Button>
										<Menu
											anchorEl={anchorEl}
											open={Boolean(anchorEl) && menuIndex === index}
											onClose={handleMenuClose}
											disableScrollLock={true}
											slotProps={{
												paper: {
													elevation: 0,
													sx: {
														overflow: "visible",
														filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
														mt: 1.5,
														"& .MuiAvatar-root": {
															width: 32,
															height: 32,
															ml: -0.5,
															mr: 1,
														},
														"&::before": {
															content: '""',
															display: "block",
															position: "absolute",
															top: 0,
															left: 14,
															width: 10,
															height: 10,
															bgcolor: "background.paper",
															transform: "translateY(-50%) rotate(45deg)",
															zIndex: 0,
														},
													},
												},
											}}
											transformOrigin={{ horizontal: "left", vertical: "top" }}
											anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
										>
											{link.subMenu.map((subLink, subIndex) => (
												<MenuItem
													key={subIndex}
													onClick={() => {
														navigate(subLink.path);
														handleMenuClose();
													}}
												>
													<b>{subLink.label}</b>
												</MenuItem>
											))}
										</Menu>
									</Box>
								) : (
									<Button
										key={index}
										sx={{ px: "8px" }}
										color="inherit"
										onClick={() => {
											if (link.path === ROUTES.DOCUMENT) {
												handleDocumentationNavigate();
											} else {
												navigate(link.path);
											}
										}}
									>
										{link.label}
									</Button>
								)
							)}
						</Box>
						<Box data-tour="main-page-header-tools" sx={{ display: "flex", flexDirection: "row", alignItems: "center", columnGap: "10px" }}>

							<CartIcon />
							<NotificationBell />
							{setChatOpen && showChatbot && (
								<Tooltip title={chatOpen ? "Close CHIP" : "Ask CHIP"} arrow placement="bottom">
									<Button
										variant="roundedGreyBgSquareContained"
										color="primary"
										onClick={() => setChatOpen((prev) => !prev)}
										sx={chatOpen ? { bgcolor: (theme) => `${theme.palette.primary.main}1A` } : {}}
									>
										<img src="/chip-icon.png" alt="CHIP" style={{ width: 22, height: 22, objectFit: "contain", borderRadius: "50%" }} />
									</Button>
								</Tooltip>
							)}
							<Tooltip title={t('LABELS.CONTACT_CCR')} arrow placement="bottom">
								<Button variant="roundedGreyBgSquareContained" color="primary" onClick={() => setCcrOpen(true)}>
									<EmailOutlinedIcon />
								</Button>

							</Tooltip>
							{!hideSearchAndSiteSelect && <GlobalSearch width="230px" />}
							{!hideSearchAndSiteSelect && <SiteSelection />}
							<IconButton color="inherit" onClick={handleProfileClick}>
								<Avatar {...stringAvatar(`${user?.user_first_name} ${user?.user_last_name}`, 40, 40, 16)} />
							</IconButton>
							<Menu
								anchorEl={profileAnchorEl}
								open={Boolean(profileAnchorEl)}
								onClose={handleProfileClose}
								disableScrollLock={true}
								slotProps={{
									paper: {
										elevation: 0,

										sx: {
											overflow: "visible",
											minWidth: '200px',
											borderRadius: "10px", //   Added border radius
											filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
											mt: 1.25,
											"& .MuiAvatar-root": {
												width: 32,
												height: 32,
												mr: 1,
											},
											"&::before": {
												content: '""',
												display: "block",
												position: "absolute",
												top: 0,
												right: 14,
												width: 10,
												height: 10,
												bgcolor: "background.paper",
												transform: "translateY(-50%) rotate(45deg)",
												zIndex: 0,
											},
										},
									},
								}}
								transformOrigin={{ horizontal: "right", vertical: "top" }}
								anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "flex-start",
										px: 1.75,
										py: 0.5,
										width: "100%",
									}}
								>
									<UserName name={user?.user_display_name} email={user?.user_email || "N/A"} />
								</Box>

								<Divider sx={{ my: 1 }} />

								{profileMenuItems.map((item, index) => {
									const isLogout = item?.label === t("LABELS.LOGOUT");

									return (
										<React.Fragment key={index}>
											{item.divider && <Divider sx={{ my: 1 }} />}

											<MenuItem
												onClick={() => handleProfileNavigate(item.label)}
												sx={
													isLogout
														? {
															color: "error.main",
															"&:hover": {
																bgcolor: (theme) => alpha(theme.palette.error.light, 0.1),
															},
														}
														: {}
												}
											>
												<ListItemIcon
													sx={
														isLogout
															? {
																color: "error.main",
															}
															: {}
													}
												>
													{item.icon}
												</ListItemIcon>

												<Typography variant="body1" fontSize={"13px"} fontWeight={"500"}>
													{item.label}
												</Typography>
											</MenuItem>
										</React.Fragment>
									);
								})}
							</Menu>
						</Box>
					</>
				) : (
					<>
						<IconButton color="inherit" edge="end" onClick={toggleMobileMenu}>
							<MenuIcon />
						</IconButton>

						<Drawer anchor="top" open={mobileOpen} onClose={toggleMobileMenu}>
							<Box
								className="header-mobile-menu"
								role="presentation"
								onClick={toggleMobileMenu}
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										flexDirection: "column",
										alignItems: "center",
									}}>
									{!hideSearchAndSiteSelect && <GlobalSearch width="250px" onClick={(e) => e.stopPropagation()} />}

									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											flexDirection: "row",
											alignItems: "center",
											width: "100%",
										}}>
										{!hideSearchAndSiteSelect && <SiteSelection />}
										<Box
											sx={{
												display: "flex",
												justifyContent: "flex-end",
												flexDirection: "row",
												alignItems: "center",
											}}>
											{setChatOpen && showChatbot && (
												<IconButton color="inherit" onClick={() => setChatOpen((prev) => !prev)}>
													<img src="/chip-icon.png" alt="CHIP" style={{ width: 22, height: 22, objectFit: "contain", borderRadius: "50%" }} />
												</IconButton>
											)}

											<IconButton color="inherit" onClick={() => setCcrOpen(true)}>
												<EmailOutlinedIcon />
											</IconButton>

											{hasTourSteps() && (
												<IconButton color="inherit" onClick={handleStartTour}>
													<SchoolIcon />
												</IconButton>
											)}

											<IconButton color="inherit" onClick={handleHelpOpen}>
												<HelpOutlineIcon />
											</IconButton>

											<IconButton color="inherit" onClick={handleProfileClick}>
												<AccountCircleIcon />
											</IconButton>

											<Menu
												anchorEl={profileAnchorEl}
												open={Boolean(profileAnchorEl)}
												onClose={handleProfileClose}>
												{canSeeAdmin && (
													<MenuItem onClick={() => navigate(ROUTES.ADMIN)}>{t('MENU_ITEMS.ADMIN')}</MenuItem>
												)}
												<MenuItem onClick={() => navigate(ROUTES.PROFILE)}>{t('LABELS.PROFILE')}</MenuItem>
												<MenuItem onClick={() => navigate(ROUTES.SUPPORT)}>{t('LABELS.SUPPORT')}</MenuItem>
												{canSeeOrderHistory && (
													<MenuItem onClick={() => navigate(ROUTES.ORDER_HISTORY)}>{t('LABELS.ORDER_HISTORYS')}</MenuItem>
												)}
												<MenuItem onClick={() => navigate(ROUTES.LOGOUT)}>{t('LABELS.LOGOUT')}</MenuItem>
											</Menu>
										</Box>
									</Box>
								</Box>

								<Divider />

								<div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
									{Navlinks.map((link, index) => (
										<div key={index} style={{ textAlign: "center" }}>
											<Button
												color="inherit"
												onClick={(event) => handleMobileNavClick(link, event)}>
												{link.label}
												{link.subMenu && <ArrowDropDownIcon />}
											</Button>

											{link.subMenu && activeMobileSubmenu === link.label && (
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														backgroundColor: "#f3f3f3",
													}}>
													{link.subMenu.map((submenuItem, subIndex) => (
														<Button
															key={subIndex}
															color="inherit"
															onClick={() => navigate(submenuItem.path)}>
															{submenuItem.label}
														</Button>
													))}
												</div>
											)}
										</div>
									))}
								</div>
								<Divider />
								<IconButton color="inherit" size="large">
									<CartIcon />
								</IconButton>
							</Box>
						</Drawer>
					</>
				)}
			</Toolbar>
			{/* Contact CCR modal opened from header */}
			<ContactCCRForm open={ccrOpen} setOpen={setCcrOpen} />
		</AppBar>
	);
};

export default Header;
